<template>
  <div
    class="sidd-chat-window"
    :class="{
      'sidd-chat-window--open': isOpen,
      'sidd-chat-window--closed': !isOpen,
      'sidd-chat-window--fill-parent': shouldFillParent,
    }"
    :data-position="mappedPosition"
  >
    <div class="sidd-chat-container">
      <ChatHeader
        @close="handleClose"
        @minimize="handleMinimize"
        @back="handleBack"
        @menu="handleMenu"
        @source="handleSource"
      />

      <div class="sidd-chat-content">
        <MessageList
          :messages="messages"
          :historical-messages="historicalMessages"
          :is-loading="isSendingMessage"
          :show-typing="showTyping"
          :auto-scroll="false"
          :on-question-click="handleSendMessage"
          :[WIDGET_CONSTANTS.DATA_ATTRIBUTES.CHAT_MESSAGES]="true"
          class="sidd-chat-messages"
        />
      </div>

      <MessageInput :placeholder="inputPlaceholder" class="sidd-chat-input" />
    </div>

    <Transition name="sources-slide">
      <SourcesList
        v-if="isSourcesListOpen"
        v-model:is-open="isSourcesListOpen"
        :sources="sourceDocuments"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useChatbot } from '@/composables/useChatbot'
import { useChatSession } from '@/composables/useChatSession'
import { useAnalytics } from '@/composables/useAnalytics'
import { wait } from '@/utils/time-utils'
import { logger } from '@/utils/logger'
import { WIDGET_CONSTANTS } from '@/types/constants'
import { ANALYTICS_EVENTS, ANALYTICS_SOURCE } from '@/types/analytics-types'
import { Position } from '@/types/chatbot-types'
import ChatHeader from '../ChatHeader/ChatHeader.vue'
import MessageList from '../MessageList/MessageList.vue'
import MessageInput from '../MessageInput/MessageInput.vue'
import { SourcesList } from '../SourcesList'
import type { IChatWindowProps, IChatWindowEmits } from './types'

const props = defineProps<IChatWindowProps>()

const emit = defineEmits<IChatWindowEmits>()

const chatbot = useChatbot()
const chatSession = useChatSession({
  persistMessages: true,
  autoScrollToBottom: false,
})
const analytics = useAnalytics()

const shouldFillParent = computed(
  () => chatbot.store.configuration?.position === Position._FillParent
)

const mappedPosition = computed(() => {
  const position = chatbot.store.configuration?.position
  if (!position) return WIDGET_CONSTANTS.DEFAULT_POSITION
  return (
    WIDGET_CONSTANTS.POSITION_MAPPING[
      position as keyof typeof WIDGET_CONSTANTS.POSITION_MAPPING
    ] || WIDGET_CONSTANTS.DEFAULT_POSITION
  )
})

const messages = computed(() => chatSession.store.messages || [])
const historicalMessages = computed(
  () => chatSession.store.historicalMessages || []
)
const isConfigLoading = computed(
  () => !chatbot.store.isInitialized || chatSession.store.isLoading || false
)
const isSendingMessage = computed(() => chatSession.store.isLoading || false)
const showTyping = computed(() => chatSession.store.isTyping || false)
const currentSessionId = computed(() => chatSession.store.activeSessionId)
const inputPlaceholder = computed(() => 'Type your message...')
const sourceDocuments = computed(() => chatSession.store.sourceDocuments || [])

const isSourcesListOpen = ref(false)

const canSendMessage = computed(
  () =>
    chatbot.store.configuration?.isActive &&
    chatSession.canSendMessage.value &&
    !isConfigLoading.value
)

const handleClose = () => {
  if (props.isOpen) {
    emit('close')
  }
}

const handleMinimize = () => {
  emit('minimize')
}

const handleBack = () => {
  emit('back')
}

const handleMenu = () => {
  emit('menu')
}

const handleSource = () => {
  isSourcesListOpen.value = true

  analytics.trackEvent({
    eventName: ANALYTICS_EVENTS.SOURCE_PANEL_OPENED,
    timestamp: new Date().toISOString(),
    payload: {
      totalSources: sourceDocuments.value.length,
      sourceNames: sourceDocuments.value.map(s => s.name),
      trigger: 'header_button',
      sessionId: chatSession.store.activeSessionId || '',
    },
    source: ANALYTICS_SOURCE,
    sessionId: chatSession.store.activeSessionId || undefined,
  })
}

const handleSendMessage = async (
  message: string,
  isSuggestedQuestion: boolean = false,
  isFollowUpQuestion: boolean = false,
  parentMessageId?: string
) => {
  if (!message.trim()) {
    return
  }

  if (!canSendMessage.value) {
    return
  }

  try {
    if (!chatSession.store.activeSessionId && chatbot.store.id) {
      const sessionId = await chatSession.startSession(chatbot.store.id)
      if (!sessionId) {
        logger.error(
          'Failed to start chat session',
          {},
          { component: 'ChatWindow' }
        )
        return
      }
    }

    const sentMessage = await chatSession.sendMessage(
      message.trim(),
      isSuggestedQuestion,
      isFollowUpQuestion,
      parentMessageId
    )

    if (sentMessage) {
      emit('message-sent', sentMessage)

      if (currentSessionId.value) {
        emit('session-started', currentSessionId.value)
      }
    }
  } catch (error) {
    logger.error(
      'Failed to send message',
      error,
      { component: 'ChatWindow' },
      error as Error
    )
  }
}

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen && chatbot.store.id) {
      await wait(100)

      if (props.initialMessage && messages.value.length === 0) {
        await handleSendMessage(props.initialMessage)
      }
    }
  }
)
</script>

<style>
.sidd-chat-window {
  position: fixed;
  width: 384px;
  height: 600px;
  background: hsl(var(--background));
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.15),
    0 10px 10px -5px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  z-index: var(--widget-z-index);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(100%) scale(0.95);
  opacity: 0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  backdrop-filter: blur(8px);
}

.sidd-chat-window--fill-parent {
  position: relative;
  width: 100% !important;
  height: 100% !important;
  transform: none;
  opacity: 1;
  pointer-events: auto;
}

.sidd-chat-window--open {
  transform: translateY(0) scale(1);
  opacity: 1;
  pointer-events: auto;
}

.sidd-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.sidd-chat-content {
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.sidd-chat-messages {
  height: 100%;
  overflow: hidden;
}

.sidd-chat-input {
  flex-shrink: 0;
}

.sidd-chat-window--fill-parent .sidd-chat-content,
.sidd-chat-window--fill-parent .sidd-chat-input {
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.sources-slide-enter-active,
.sources-slide-leave-active {
  transition: opacity 0.3s ease;
}

.sources-slide-enter-active .sources-list__overlay,
.sources-slide-leave-active .sources-list__overlay {
  transition: opacity 0.3s ease;
}

.sources-slide-enter-active .sources-list__panel,
.sources-slide-leave-active .sources-list__panel {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sources-slide-enter-from,
.sources-slide-leave-to {
  opacity: 0;
}

.sources-slide-enter-from .sources-list__panel,
.sources-slide-leave-to .sources-list__panel {
  transform: translateX(100%);
}

.sources-slide-enter-to,
.sources-slide-leave-from {
  opacity: 1;
}

.sources-slide-enter-to .sources-list__panel,
.sources-slide-leave-from .sources-list__panel {
  transform: translateX(0);
}
</style>
